import { Image } from 'expo-image';
import { useLocalSearchParams, useRouter } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import mixpanelService from '@/core/clients/mixpanel';
import useSettingsStore from '@/stores/use-settings-store';
import { Button, Card, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Checkmark } from '@/ui/icons/checkmark';
import { QuestionMark } from '@/ui/icons/question-mark';

const darkImg = require('@/../public/images/subscriptions/onboarding-dark.png');
const lightImg = require('@/../public/images/subscriptions/onboarding-light.png');

const items = [
  'Premium EVs you can’t get anywhere else',
  'Free delivery',
  'Keyless unlock via app',
  'Real human support 24/7',
];

export default function Onboarding() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const local = useLocalSearchParams<{ code?: string }>();
  const [currentStep, setCurrentStep] = useState(1);
  const setSubscriptionTutorialSeen = useSettingsStore(
    (state) => state.setSubscriptionTutorialSeen
  );

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Subscriptions Onboarding',
    });
  }, []);

  const renderBody = useMemo(() => {
    if (currentStep === 2) {
      return (
        <View style={styles.body}>
          <Text type="title1" style={{ textAlign: 'center' }}>
            How Eon Subscription Works
          </Text>

          <View style={{ gap: 16 }}>
            <Card style={{ padding: 16, gap: 16 }}>
              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Step 1
              </Text>

              <View>
                <Text type="bodyBold">Pick Your Car</Text>

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Choose from premium electric vehicles designed to fit your
                  lifestyle.
                </Text>
              </View>
            </Card>

            <Card style={{ padding: 16, gap: 16 }}>
              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Step 2
              </Text>

              <View>
                <Text type="bodyBold">Choose Your Mileage and Insurance</Text>

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Select your monthly mileage tier and insurance option.
                </Text>
              </View>
            </Card>

            <Card style={{ padding: 16, gap: 16 }}>
              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Step 3
              </Text>

              <View>
                <Text type="bodyBold">Drive With Full Control</Text>

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Use the car like it's yours. Unlock, lock, locate, and manage
                  everything from your phone. Need more miles? Upgrade your
                  mileage tier in the app.
                </Text>
              </View>
            </Card>

            <View
              style={{ flexDirection: 'row', gap: 4, justifyContent: 'center' }}
            >
              <Icon
                type="naked"
                icon={<QuestionMark />}
                fillColor={theme.colors.icon.color}
                width={12}
                height={12}
              />

              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Questions about subscriptions?
              </Text>

              <Pressable
                onPress={async () => {
                  const baseUrl =
                    'https://www.eonrides.com/legal/subscription-faq';

                  if (Platform.OS === 'web') {
                    window.open(baseUrl, '_blank');
                  } else {
                    await WebBrowser.openBrowserAsync(baseUrl);
                  }
                }}
              >
                <Text
                  type="caption1"
                  style={{
                    color: theme.colors.text.secondary,
                    textDecorationLine: 'underline',
                  }}
                >
                  View FAQ
                </Text>
              </Pressable>
            </View>
          </View>

          <Button
            text="Get Started"
            type="primary"
            onPress={() => {
              setSubscriptionTutorialSeen(true);
              if (router.canGoBack()) {
                router.back();
              } else {
                router.replace({
                  pathname: '/subscriptions',
                  ...(local.code
                    ? { params: { code: String(local.code) } }
                    : {}),
                });
              }
            }}
          />
        </View>
      );
    }

    return (
      <View style={styles.body}>
        <Image
          source={UnistylesRuntime.themeName === 'dark' ? darkImg : lightImg}
          contentFit="contain"
          style={{
            width: '100%',
            height: 150,
          }}
        />

        <View style={{ gap: 8 }}>
          <Text type="title1" style={{ textAlign: 'center' }}>
            Car Subscriptions Made Simple.
          </Text>

          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary, textAlign: 'center' }}
          >
            Drive electric without the hassle of owning. Subscriptions built for
            modern freedom.
          </Text>
        </View>

        <View>
          <Card style={{ padding: 16, gap: 8 }}>
            <Text type="bodyBold">
              No long contracts, no surprise costs, just flexibility that moves
              with you.
            </Text>

            <View style={{ gap: 4 }}>
              {items.map((item) => {
                return (
                  <View
                    key={item}
                    style={{
                      flexDirection: 'row',
                      gap: 8,
                    }}
                  >
                    <View style={{ marginTop: 4 }}>
                      <Icon
                        type="naked"
                        icon={<Checkmark />}
                        fillColor={theme.colors.success}
                        width={12}
                        height={12}
                      />
                    </View>

                    <Text
                      type="bodyRegular"
                      style={{
                        color: theme.colors.text.secondary,
                        flexShrink: 1,
                      }}
                    >
                      {item}
                    </Text>
                  </View>
                );
              })}
            </View>
          </Card>
        </View>

        <Button
          text="Next"
          type="primary"
          onPress={() => {
            setCurrentStep(2);
          }}
        />
      </View>
    );
  }, [currentStep]);

  return (
    <View style={styles.container}>
      {/* {!isDesktop() ? (
        <ProgressIndicator currentStep={currentStep} numSteps={2} />
      ) : null} */}

      {renderBody}
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
    paddingTop: UnistylesRuntime.insets.top,
  },
  body: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    padding: 16,
    gap: 32,
    width: {
      xs: '100%',
      md: 600,
    },
  },
}));
